import { Button } from "~/components/ui/button";
import { FileText, Newspaper, SearchX } from "lucide-react";

// // // //

/**
 * CitationsListEmptyState
 * Empty state for the citations ledger — a blueprint-style canvas with
 * floating publication elements behind a centered clear-filters CTA.
 */
export function CitationsListEmptyState(props: { onClearFilters: () => void }) {
    const { onClearFilters } = props;

    return (
        <div className="min-w-0 overflow-hidden rounded-xl border bg-card shadow-xs">
            <div className="relative flex min-h-120 w-full items-center justify-center overflow-hidden">
                {/* Subtle Dotted Background Canvas */}
                <div className="absolute inset-0 z-0 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] opacity-40 bg-size-[32px_32px]"></div>

                {/* Floating Blueprint Elements */}
                <div className="absolute left-16 top-16 z-10 hidden -rotate-6 rounded-2xl border bg-card/80 p-4 opacity-60 shadow-sm backdrop-blur-md md:block">
                    <Newspaper
                        className="mb-6 h-6 w-6 text-muted-foreground"
                        strokeWidth={1.5}
                    />
                    <div className="space-y-1.5">
                        <div className="h-1 w-12 rounded-full bg-muted-foreground"></div>
                        <div className="h-1 w-8 rounded-full bg-muted-foreground"></div>
                    </div>
                </div>

                <div className="absolute bottom-16 right-16 z-10 hidden rotate-12 rounded-2xl border bg-card/80 p-4 opacity-60 shadow-sm backdrop-blur-md md:block">
                    <FileText
                        className="mb-6 h-6 w-6 text-muted-foreground"
                        strokeWidth={1.5}
                    />
                    <div className="space-y-1.5">
                        <div className="h-1 w-14 rounded-full bg-muted-foreground"></div>
                        <div className="h-1 w-9 rounded-full bg-muted-foreground"></div>
                    </div>
                </div>

                {/* Central Glow for depth */}
                <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full bg-card blur-2xl"></div>
                <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-50/50 blur-[80px]"></div>

                {/* Main Content */}
                <div className="relative z-20 flex max-w-md flex-col items-center px-6 py-12 text-center">
                    <div className="relative mb-8">
                        <div className="absolute inset-0 rounded-3xl bg-blue-100/50 blur-xl"></div>
                        <div className="relative flex h-20 w-20 items-center justify-center rounded-3xl border bg-card shadow-sm">
                            <SearchX
                                className="h-9 w-9 text-muted-foreground"
                                strokeWidth={1.5}
                            />
                        </div>
                    </div>

                    <h2 className="mb-3 text-2xl font-medium tracking-tight text-foreground">
                        No publications found
                    </h2>

                    <p className="mx-auto mb-8 max-w-sm text-[15px] leading-relaxed text-muted-foreground">
                        Try a different search term, or widen your search to see
                        more of the publications &amp; reports.
                    </p>

                    <Button
                        variant="primaryLight"
                        size="lg"
                        // className="rounded-lg text-base"
                        onClick={() => onClearFilters()}
                    >
                        Clear Search
                    </Button>
                </div>

                {/* Fade out edges slightly to frame the grid */}
                <div className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,white_100%)]"></div>
            </div>
        </div>
    );
}
